import * as Sentry from "@sentry/browser";
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import ErrorMessages from '../../../components/UI/ErrorMessages';
import { getNewTransactionEventData, redirectToPaymentGateway } from "../../../helpers/planTransaction";
import { flush, track } from "../../../helpers/track";
import { PaymentGateway as PaymentGatewayType } from "../../../services/paymentsGatewayGet";
import { PlanResponse } from '../../../services/plansGet';
import planTransactionPost from "../../../services/planTransactionPost";
import { fetchPaymentsGateway as fetchPaymentsGatewayAction } from "../../../store/paymentsGateway/actions";
import { PaymentsGatewayState } from "../../../store/paymentsGateway/types";
import { fetchPlans as fetchPlansAction } from '../../../store/plans/action';
import { PlansState } from '../../../store/plans/types';
import { AppState } from '../../../store/store';
import ModalSuccessCode from '../components/ModalSuccessCode';
import NewPaymentMethodSelector from '../components/NewPaymentMethodSelector';
import NewPaymentSummary from '../components/NewPaymentSummary';
import NewPlanSelector from '../components/NewPlanSelector';
import PaymentCode, { OnRedeemParams } from "../components/PaymentCode";
import styles from './NewPlansContainer.module.scss';

interface Props extends RouteComponentProps {
  plansState: PlansState;
  paymentsGatewayState: PaymentsGatewayState;
  userId: string;
  fetchPlans(): void;
  fetchPaymentsGateway(): void;
  onClose: () => void;
}

const NewPlansContainer = ({ plansState, paymentsGatewayState, userId, fetchPlans, fetchPaymentsGateway, history, onClose, location }: Props) => {
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<PlanResponse | null>(null);
  const [coupon, setCoupon] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<PaymentGatewayType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [planCost, setPlanCost] = useState<number | null>(null);
  const [teaser, setTeaser] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchPlans();
    fetchPaymentsGateway();
  }, [fetchPlans, fetchPaymentsGateway]);

  useEffect(() => {
    if (plansState.status === 'loaded') {
      const loadedPlans = plansState.payload;
      setPlans(loadedPlans);

      const queryParams = new URLSearchParams(location.search);
      const couponParam = queryParams.get('coupon');

      let defaultPlan;

      if (couponParam === 'BLACK25') {
        defaultPlan = loadedPlans.find(p => p.name.includes('Tu Comienzo'));
      }

      if (!defaultPlan) {
        defaultPlan = loadedPlans.find(p => p.name.includes('Tu Comienzo')) || loadedPlans[0];
      }

      if (defaultPlan) {
        setSelectedPlan(defaultPlan);
        setPlanCost(defaultPlan.cost);
      }
    }
  }, [plansState, location.search]);

  useEffect(() => {
    if (paymentsGatewayState.status === 'loaded') {
      const gateways = paymentsGatewayState.payload.paymentsGateway;
      if (gateways && gateways.length > 0 && !selectedMethod) {
        setSelectedMethod(gateways[0]);
      }
    }
  }, [paymentsGatewayState, selectedMethod]);

  const handleSelectPlan = (plan: PlanResponse) => {
    setSelectedPlan(plan);
    setPlanCost(plan.cost);
    setCoupon('');
    setError('');
  };

  const handleRedeem = ({ code, teaser, value }: OnRedeemParams) => {
    setCoupon(code);
    setPlanCost(value);
    setTeaser(teaser);

    if (value === 0) {
      setShowSuccessModal(true);
    }
    setTimeout(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 100);
  };

  const handleSelectMethod = (method: PaymentGatewayType) => {
    setSelectedMethod(method);
  };

  const handlePay = async () => {
    if (isSubmitting) return;

    if (!selectedPlan || !selectedMethod || planCost === null) return;

    try {
      setError("");
      setIsSubmitting(true);

      const transaction = await planTransactionPost({
        planUuid: selectedPlan.uuid,
        paymentGateway: selectedMethod.code,
        promoCode: coupon,
      });

      const { payload } = transaction;
      if (payload) {
        const properties = { coupon, plan: selectedMethod.name, gtw: selectedMethod.code, ...getNewTransactionEventData(selectedMethod.code, payload) };
        track({ userId, event: "NEW_PLAN_TRANSACTION", properties });
        try {
          await flush();
        } catch (flushError) {
          Sentry.captureException(flushError);
        }
        redirectToPaymentGateway(payload);
      }
    } catch {
      setError("Sucedió un error, inténtalo más tarde.");
      setIsSubmitting(false);
    };
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Elige tu consulta</h2>
        <button className={styles.closeButton} onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
        </button>
      </div>

      <NewPlanSelector plans={plans} selectedPlan={selectedPlan} onSelectPlan={handleSelectPlan} />
      {selectedPlan && !coupon && (
        <PaymentCode
          planUuid={selectedPlan.uuid}
          onRedeem={handleRedeem}
          initialCode={new URLSearchParams(location.search).get('coupon') || undefined}
        />
      )}
      <NewPaymentMethodSelector selectedMethod={selectedMethod} onSelectMethod={handleSelectMethod} />
      {planCost !== null && selectedPlan && <NewPaymentSummary planName={selectedPlan.name} cost={planCost} originalCost={selectedPlan.cost} />}
      {error && <ErrorMessages errorMessages={[error]} />}

      <button
        className={styles.payButton}
        onClick={handlePay}
        disabled={isSubmitting || !selectedPlan || !selectedMethod || planCost === null}
      >
        {isSubmitting ? (
          <>
            <div className="spinner-border spinner-border-sm" role="status" />
            <span style={{ marginLeft: '8px' }}>Procesando...</span>
          </>
        ) : 'Pagar Ahora'}
      </button>

      <div className={styles.securePayment}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path></svg>
        <span>Pago 100% seguro y protegido</span>
      </div>
      {showSuccessModal && <ModalSuccessCode teaser={teaser} />}
      <div ref={bottomRef} />
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  plansState: state.plans,
  paymentsGatewayState: state.paymentsGateway,
  userId: state.security.userId,
});

const mapDispatchToProps = {
  fetchPlans: fetchPlansAction,
  fetchPaymentsGateway: fetchPaymentsGatewayAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(NewPlansContainer));